import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import InputFormField from 'src/registry/components/forms/InputFormField';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import { Form, FormDescription } from 'src/registry/ui/Form';
import { useListUnits } from 'src/features/units/api/useListUnits';
import { unitsMessages } from 'src/features/units/utils/messages';
import { commonMessages } from 'src/utils/messages';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { ServicePlanItemFormSchemaValues } from 'src/features/servicePlanItems/utils/formSchema';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/moneyAccounts/utils/constants';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { useListBillableMetrics } from 'src/features/billableMetrics/api/useListBillableMetrics';
import { billableMetricsMessages } from 'src/features/billableMetrics/utils/messages';

type ServicePlanItemFormProps = {
    form: UseFormReturn<ServicePlanItemFormSchemaValues>;
    onSubmit: (values: ServicePlanItemFormSchemaValues) => void;
};

function ServicePlanItemForm({ onSubmit, form }: ServicePlanItemFormProps) {
    const { formatMessage } = useIntl();

    const { data: units } = useListUnits({});
    const { data: billableMetrics } = useListBillableMetrics({ limit: 500 });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<ServicePlanItemFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(commonMessages.name)}
            />
            <InputFormField<ServicePlanItemFormSchemaValues>
                form={form}
                name="code"
                label={formatMessage(servicePlansMessages.code)}
            />
            <SelectFormField<ServicePlanItemFormSchemaValues>
                form={form}
                name="is_required"
                isBoolean={true}
                label={formatMessage(servicePlanItemsMessages.isRequired)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
            <SelectFormField<ServicePlanItemFormSchemaValues>
                form={form}
                name="allows_price_coefficient"
                isBoolean={true}
                label={formatMessage(servicePlanItemsMessages.allowsPriceCoefficient)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
            <FormDescription>{formatMessage(servicePlanItemsMessages.allowsPriceCoefficientHint)}</FormDescription>
            <SearchSelectFormField
                name="billable_metric"
                control={form.control}
                required={false}
                clearable
                label={formatMessage(servicePlanItemsMessages.metric)}
                items={billableMetrics?.results ?? []}
                placeholder={`${formatMessage(servicePlanItemsMessages.metric)}...`}
                searchPlaceholder={formatMessage(servicePlanItemsMessages.metric)}
                emptyTitle={formatMessage(billableMetricsMessages.billableMetrics)}
                getItemValue={(metric) => metric.id}
                getItemLabel={(metric) => `${metric.name} (${metric.code})`}
                getItemKeywords={(metric) => [metric.name, metric.code]}
            />
            <FormDescription>{formatMessage(servicePlanItemsMessages.metricHint)}</FormDescription>
            <InputFormField<ServicePlanItemFormSchemaValues>
                form={form}
                name="external_item_id"
                required={false}
                label={formatMessage(servicePlanItemsMessages.externalId)}
            />
            <InputFormField<ServicePlanItemFormSchemaValues>
                form={form}
                name="starting_quantity"
                type="number"
                required={false}
                label={formatMessage(servicePlanItemsMessages.startingQuantity)}
            />
            <SearchSelectFormField
                name="unit"
                control={form.control}
                required={false}
                clearable
                label={formatMessage(unitsMessages.unit)}
                items={units?.results ?? []}
                placeholder={`${formatMessage(unitsMessages.unit)}...`}
                searchPlaceholder={formatMessage(unitsMessages.unit)}
                emptyTitle={formatMessage(unitsMessages.units)}
                getItemValue={(unit) => unit.id}
                getItemLabel={(unit) => `${unit.name} (${unit.abbreviation})`}
                getItemKeywords={(unit) => [unit.name, unit.code, unit.abbreviation]}
            />
            <FormDescription>{formatMessage(servicePlanItemsMessages.unitHint)}</FormDescription>
        </Form>
    );
}

export default ServicePlanItemForm;
